import { canonicalUrl } from "./canonicalUrl";

// The SEO head model (ADR-0053 §1, §5). A pure `(input) → HeadDescriptor`
// consumed by a route's Start `head()` and baked into the prerendered `<head>`
// — the deterministic replacement for the old runtime `useEffect` head hooks
// (`features/landing/seo.ts`), which social scrapers (Facebook/LinkedIn/Slack/X)
// never saw because they do not run JS.
//
// Every URL it emits (`canonical`, `og:url`, `og:image`, and the JSON-LD `url`s)
// is absolute, derived from a per-environment build-time `SITE_URL` passed in as
// `siteUrl` — never `window.location`. Structured data follows ADR-0053 §5:
// `Organization` + `WebSite` sitewide (stable, no fabricated data), `Product` +
// `Offer` ONLY when a real price is present, and `AggregateRating` NEVER (there
// are no real reviews). Kept side-effect-free and framework-agnostic so it is
// unit-testable and identical under prerender, hydration, and client navigation.

/** The stable brand identity emitted in `Organization`/`WebSite`. */
export const ORG_NAME = "Pufimi";
/** Real brand mark under `/public` — used as the `Organization` logo (honest,
 *  not fabricated). Kept root-relative; made absolute via `SITE_URL`. */
const LOGO_PATH = "/favicon.svg";

/**
 * Browser-tab `<title>` for a non-indexed app shell (`/upload`, `/checkout/*`,
 * `/me`, …): page name first, brand suffix — e.g. `"Zamówienie · Pufimi"`.
 *
 * These routes are `noindex` and NOT prerendered — they carry
 * `X-Robots-Tag: noindex` via `_headers` and are absent from the sitemap — so they
 * need a correct title for the browser tab and history but NOT the full `buildHead`
 * (no canonical / OG / JSON-LD, which would be wrong on a noindex page). Give each
 * such route a `head()` returning `{ meta: [{ title: appShellTitle(...) }] }`:
 * without it, a hard load rewrites to the `/` shell (`_redirects`) and the LANDING
 * title sticks until the client re-titles — the `/upload` wrong-title bug.
 */
export function appShellTitle(pageName: string): string {
  return `${pageName} · ${ORG_NAME}`;
}

/** A schema.org JSON-LD node. Values stay JSON-serialisable. */
type JsonLd = Record<string, unknown>;

export type TwitterCard = "summary" | "summary_large_image";

/** A real, non-fabricated price. Absent/`null` → no `Product`/`Offer` at all. */
export interface SeoPrice {
  price: number;
  currency: string;
}

export interface SeoImage {
  /** Root-relative path under `/public` (or an absolute URL); made absolute. */
  path: string;
  /** Localised alt/voice for `og:image:alt`. */
  alt?: string;
}

/** The localised, per-route SEO content — the same strings the page renders. */
export interface SeoContent {
  title: string;
  description: string;
  image?: SeoImage;
  /** Defaults to `summary_large_image` when an image is present, else `summary`. */
  twitterCard?: TwitterCard;
  /** Open Graph object type. Defaults to `website`. */
  ogType?: string;
  /** Emitted as `Product` + `Offer` ONLY when non-null (ADR-0053 §5). */
  product?: SeoPrice | null;
}

export interface BuildHeadInput {
  /** The route path used for the canonical URL, e.g. `"/"` or `"/comparison"`. */
  path: string;
  /** Active UI locale (`pl` | `en` | `de`), for `og:locale` + JSON-LD language. */
  locale: string;
  /** Per-environment absolute base (staging host / `pufimi.pl`); never runtime. */
  siteUrl: string;
  content: SeoContent;
}

/** A single meta entry in Start's head model (a `<meta>`/`<title>` tag). */
export type SeoMeta =
  | { title: string }
  | { name: string; content: string }
  | {
      property: string;
      content: string;
    };

export interface SeoLink {
  rel: string;
  href: string;
}

/** A head `<script>` tag. Start's `head().scripts` are HEAD scripts, so JSON-LD
 *  emitted here is baked into the prerendered `<head>` (ADR-0053 §5). */
export interface SeoScript {
  type: "application/ld+json";
  children: string;
}

/** Assignable to a Start route's `head()` return (`{ meta, links, scripts }`),
 *  where `scripts` are head scripts. */
export interface HeadDescriptor {
  meta: SeoMeta[];
  links: SeoLink[];
  scripts: SeoScript[];
}

// Open Graph wants a `language_TERRITORY` tag; our three UI locales map to their
// conventional pairs. PL is the launch/canonical locale (ADR-0053 §3).
const OG_LOCALE: Record<string, string> = {
  pl: "pl_PL",
  en: "en_US",
  de: "de_DE",
};

function ogLocale(locale: string): string {
  return OG_LOCALE[locale] ?? "pl_PL";
}

/** A JSON-LD node as a head `<script type="application/ld+json">` tag.
 *
 *  The serialized JSON is baked verbatim into the prerendered `<head>`, so any
 *  `<` in the data (e.g. a title or description carrying a literal `<`, or a
 *  `</script>` sequence) would otherwise close the script element early and break
 *  the page — a real hazard now that more routes route localised content through
 *  here. Escaping `<` to its `<` unicode form is inert inside JSON (parsers
 *  decode it identically) but can never be read as markup by the HTML parser. */
function ldScript(node: JsonLd): SeoScript {
  return { type: "application/ld+json", children: JSON.stringify(node).replace(/</g, "\\u003c") };
}

/** Sitewide `Organization` (ADR-0053 §5). Stable, no fabricated data. */
export function organizationJsonLd(siteUrl: string): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: ORG_NAME,
    url: canonicalUrl("/", siteUrl),
    logo: canonicalUrl(LOGO_PATH, siteUrl),
  };
}

/** Sitewide `WebSite` (ADR-0053 §5). No `SearchAction` — there is no site search. */
export function webSiteJsonLd(siteUrl: string, locale: string): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: ORG_NAME,
    url: canonicalUrl("/", siteUrl),
    inLanguage: locale,
  };
}

/** `Product` (+ `Offer`) for a route. Emitted by the caller ONLY when a real
 *  price exists (ADR-0053 §5); `AggregateRating` is deliberately never added. */
function productJsonLd(
  content: SeoContent,
  price: SeoPrice,
  siteUrl: string,
  canonical: string,
): JsonLd {
  const node: JsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: content.title,
    description: content.description,
    brand: { "@type": "Brand", name: ORG_NAME },
    offers: {
      "@type": "Offer",
      price: price.price,
      priceCurrency: price.currency,
      availability: "https://schema.org/InStock",
      url: canonical,
    },
  };
  if (content.image) {
    node.image = [canonicalUrl(content.image.path, siteUrl)];
  }
  return node;
}

export function buildHead({ path, locale, siteUrl, content }: BuildHeadInput): HeadDescriptor {
  const canonical = canonicalUrl(path, siteUrl);
  const image = content.image ? canonicalUrl(content.image.path, siteUrl) : undefined;
  const card: TwitterCard = content.twitterCard ?? (image ? "summary_large_image" : "summary");

  const meta: SeoMeta[] = [
    { title: content.title },
    { name: "description", content: content.description },
    { property: "og:type", content: content.ogType ?? "website" },
    { property: "og:site_name", content: ORG_NAME },
    { property: "og:title", content: content.title },
    { property: "og:description", content: content.description },
    { property: "og:url", content: canonical },
    { property: "og:locale", content: ogLocale(locale) },
    { name: "twitter:card", content: card },
    { name: "twitter:title", content: content.title },
    { name: "twitter:description", content: content.description },
  ];

  if (image) {
    meta.push({ property: "og:image", content: image });
    if (content.image?.alt) meta.push({ property: "og:image:alt", content: content.image.alt });
    meta.push({ name: "twitter:image", content: image });
  }

  // Sitewide structured data first, then the price-gated Product (ADR-0053 §5).
  const scripts: SeoScript[] = [
    ldScript(organizationJsonLd(siteUrl)),
    ldScript(webSiteJsonLd(siteUrl, locale)),
  ];
  if (content.product) {
    scripts.push(ldScript(productJsonLd(content, content.product, siteUrl, canonical)));
  }

  return { meta, links: [{ rel: "canonical", href: canonical }], scripts };
}
